import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Sparkles, 
  Video, 
  User, 
  Loader2, 
  RefreshCw,
  Play,
  Star,
  Target,
  TrendingUp,
  Upload,
  FileVideo,
  Trophy,
  Share2,
  Film,
  Image as ImageIcon,
  Link,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import TimelineEditor from '../components/TimelineEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAnalysis = () => {
  const { token, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('game');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  
  // Game analysis form
  const [gameForm, setGameForm] = useState({
    description: '',
    sport: 'Basketball',
    teams: '',
    player_focus: ''
  });
  const [gameAnalysis, setGameAnalysis] = useState(null);
  
  // Media upload analysis (images and videos)
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
  const [mediaForm, setMediaForm] = useState({
    sport: 'Basketball',
    analysis_type: 'general',
    player_focus: '',
    context: ''
  });
  const [mediaAnalysis, setMediaAnalysis] = useState(null);
  
  // Game log analysis
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [selectedMatch, setSelectedMatch] = useState('');
  const [gameLogAnalysis, setGameLogAnalysis] = useState(null);
  
  // Highlight reel
  const [reelForm, setReelForm] = useState({
    reel_type: 'tournament',
    tournament_id: '',
    athlete_id: '',
    duration: 60,
    style: 'hype'
  });
  const [highlightReel, setHighlightReel] = useState(null);
  const [previousReels, setPreviousReels] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  
  // Player insights
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [playerInsights, setPlayerInsights] = useState(null);
  
  // Sharing
  const [copiedId, setCopiedId] = useState(null);
  
  // Previous analyses
  const [previousAnalyses, setPreviousAnalyses] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [athletesRes, tournamentsRes] = await Promise.all([
        fetch(`${API_URL}/api/athletes`),
        fetch(`${API_URL}/api/tournaments/selectable`)
      ]);
      
      if (athletesRes.ok) setAthletes(await athletesRes.json());
      if (tournamentsRes.ok) setTournaments(await tournamentsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  const fetchPreviousAnalyses = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    try {
      const [analysesRes, reelsRes] = await Promise.all([
        fetch(`${API_URL}/api/ai/analyses`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/ai/highlight-reels`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (analysesRes.ok) setPreviousAnalyses(await analysesRes.json());
      if (reelsRes.ok) setPreviousReels(await reelsRes.json());
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
    setRefreshing(false);
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchData();
    fetchPreviousAnalyses();
  }, [fetchData, fetchPreviousAnalyses]);

  // Pull to refresh handlers
  const handleTouchStart = (e) => {
    if (window.scrollY === 0) setTouchStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const diff = e.touches[0].clientY - touchStart;
    if (diff > 80 && !refreshing) {
      setRefreshing(true);
      fetchPreviousAnalyses();
    }
  };

  const handleTouchEnd = () => setTouchStart(null);

  // Media file handler (images and videos)
  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
      const isVideo = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'].includes(file.type);
      
      if (!isImage && !isVideo) {
        toast.error('Please upload a JPEG, PNG, WEBP image or MP4, WEBM video');
        return;
      }
      
      setMediaFile(file);
      setMediaType(isVideo ? 'video' : 'image');
      
      if (isImage) {
        const reader = new FileReader();
        reader.onloadend = () => setMediaPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setMediaPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleGameAnalysis = async (e) => {
    e.preventDefault();
    if (!gameForm.description) { toast.error('Please describe the game'); return; }
    
    setLoading(true);
    setGameAnalysis(null);
    
    try {
      const teamsArray = gameForm.teams ? gameForm.teams.split(',').map(t => t.trim()) : null;
      const res = await fetch(`${API_URL}/api/ai/analyze-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...gameForm, teams: teamsArray })
      });
      
      if (res.ok) {
        setGameAnalysis(await res.json());
        toast.success('Analysis complete!');
        fetchPreviousAnalyses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Error running analysis');
    }
    setLoading(false);
  };

  const handleMediaAnalysis = async (e) => {
    e.preventDefault();
    if (!mediaFile) { toast.error('Please upload a file'); return; }
    
    setLoading(true);
    setMediaAnalysis(null);
    
    try {
      if (mediaType === 'image') {
        // Convert image to base64
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          
          const res = await fetch(`${API_URL}/api/ai/analyze-image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ image_base64: base64, ...mediaForm })
          });
          
          if (res.ok) {
            setMediaAnalysis(await res.json());
            toast.success('Image analysis complete!');
            fetchPreviousAnalyses();
          } else {
            const error = await res.json();
            toast.error(error.detail || 'Analysis failed');
          }
          setLoading(false);
        };
        reader.readAsDataURL(mediaFile);
      } else {
        // Video analysis
        const res = await fetch(`${API_URL}/api/ai/analyze-video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            sport: mediaForm.sport,
            analysis_type: mediaForm.analysis_type === 'general' ? 'highlights' : mediaForm.analysis_type,
            player_focus: mediaForm.player_focus,
            context: mediaForm.context
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          setMediaAnalysis(data);
          toast.success('Video analysis complete!');
          fetchPreviousAnalyses();
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Analysis failed');
        }
        setLoading(false);
      }
    } catch (error) {
      toast.error('Error analyzing media');
      setLoading(false);
    }
  };

  // Share functions
  const handleShareAnalysis = async (analysisId) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/analyses/${analysisId}/share`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        const shareUrl = `${window.location.origin}${data.share_url}`;
        navigator.clipboard.writeText(shareUrl);
        setCopiedId(analysisId);
        setTimeout(() => setCopiedId(null), 2000);
        toast.success('Share link copied!');
      }
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  const handleShareReel = async (reelId) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/highlight-reels/${reelId}/share`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        const shareUrl = `${window.location.origin}${data.share_url}`;
        navigator.clipboard.writeText(shareUrl);
        setCopiedId(reelId);
        setTimeout(() => setCopiedId(null), 2000);
        toast.success('Share link copied!');
      }
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  const handleGameLogAnalysis = async (e) => {
    e.preventDefault();
    if (!selectedTournament) { toast.error('Please select a tournament'); return; }
    
    setLoading(true);
    setGameLogAnalysis(null);
    
    try {
      const res = await fetch(`${API_URL}/api/ai/analyze-game-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          tournament_id: selectedTournament, 
          match_id: selectedMatch || null 
        })
      });
      
      if (res.ok) {
        setGameLogAnalysis(await res.json());
        toast.success('Game log analysis complete!');
        fetchPreviousAnalyses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Error analyzing game log');
    }
    setLoading(false);
  };

  const handleGenerateReel = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setHighlightReel(null);
    
    try {
      const res = await fetch(`${API_URL}/api/ai/generate-highlight-reel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournament_id: reelForm.reel_type === 'tournament' ? reelForm.tournament_id : null,
          athlete_id: reelForm.reel_type === 'player' ? reelForm.athlete_id : null,
          reel_type: reelForm.reel_type,
          duration_seconds: reelForm.duration,
          style: reelForm.style
        })
      });
      
      if (res.ok) {
        const reelData = await res.json();
        setHighlightReel(reelData);
        setShowEditor(true);  // Automatically show the editor with video preview
        toast.success('Highlight reel generated!');
        fetchPreviousAnalyses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Reel generation failed');
      }
    } catch (error) {
      toast.error('Error generating reel');
    }
    setLoading(false);
  };

  const handlePlayerInsights = async (athleteId) => {
    setLoading(true);
    setSelectedAthlete(athleteId);
    setPlayerInsights(null);
    
    try {
      const res = await fetch(`${API_URL}/api/ai/player-insights?athlete_id=${athleteId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        setPlayerInsights(await res.json());
        toast.success('Insights generated!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed');
      }
    } catch (error) {
      toast.error('Error getting insights');
    }
    setLoading(false);
  };

  const selectedTournamentData = tournaments.find(t => t.id === selectedTournament);

  return (
    <div 
      className="min-h-screen bg-rooam-black"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {refreshing && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-rooam-blue text-black px-4 py-2 rounded-full flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" /> Refreshing...
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-rooam-blue" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Advanced Stats</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-400">
            AI-powered game analysis, highlight reels, and player insights
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
          <TabsList className="bg-rooam-gray-medium border border-rooam-gray-light w-full overflow-x-auto flex-nowrap">
            <TabsTrigger value="game" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <Video className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Game</span>
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <Upload className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Upload</span>
            </TabsTrigger>
            <TabsTrigger value="gamelog" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <Trophy className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Game Log</span>
            </TabsTrigger>
            <TabsTrigger value="reels" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <Film className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Reels</span>
            </TabsTrigger>
            <TabsTrigger value="player" className="data-[state=active]:bg-rooam-blue data-[state=active]:text-black text-xs sm:text-sm whitespace-nowrap">
              <User className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Player</span>
            </TabsTrigger>
          </TabsList>

          {/* Game Description Analysis Tab */}
          <TabsContent value="game">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Video className="h-5 w-5 text-rooam-blue" /> Analyze Game
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGameAnalysis} className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Game Description *</Label>
                      <textarea
                        value={gameForm.description}
                        onChange={(e) => setGameForm({ ...gameForm, description: e.target.value })}
                        className="w-full h-32 bg-rooam-black border border-rooam-gray-light rounded-md p-3 text-white text-sm"
                        placeholder="Describe the game... e.g., 'Championship game between Lakers Elite and Warriors Academy. Fast-paced, final score 78-72.'"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Sport</Label>
                        <Select value={gameForm.sport} onValueChange={(v) => setGameForm({ ...gameForm, sport: v })}>
                          <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                            <SelectItem value="Basketball">Basketball</SelectItem>
                            <SelectItem value="Football">Football</SelectItem>
                            <SelectItem value="Soccer">Soccer</SelectItem>
                            <SelectItem value="Volleyball">Volleyball</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Focus Player</Label>
                        <Input value={gameForm.player_focus} onChange={(e) => setGameForm({ ...gameForm, player_focus: e.target.value })}
                          className="bg-rooam-black border-rooam-gray-light text-white" placeholder="Optional" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-300">Teams (comma separated)</Label>
                      <Input value={gameForm.teams} onChange={(e) => setGameForm({ ...gameForm, teams: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white" placeholder="Team A, Team B" />
                    </div>
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} Analyze
                    </Button>
                  </form>
                </CardContent>
              </Card>
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Target className="h-5 w-5 text-green-400" /> Result</CardTitle></CardHeader>
                <CardContent>
                  {gameAnalysis ? (
                    <div className="bg-rooam-black rounded-lg p-4 max-h-96 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans">{gameAnalysis.analysis}</pre>
                    </div>
                  ) : (
                    <div className="text-center py-12"><Sparkles className="h-12 w-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-400">Enter game details to get AI insights</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Upload Analysis Tab */}
          <TabsContent value="upload">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Upload className="h-5 w-5 text-rooam-blue" /> Upload Media
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleMediaAnalysis} className="space-y-4">
                    <div className="border-2 border-dashed border-rooam-gray-light rounded-lg p-6 text-center hover:border-rooam-blue transition-colors">
                      <input 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" 
                        onChange={handleMediaUpload} 
                        className="hidden" 
                        id="media-upload" 
                      />
                      <label htmlFor="media-upload" className="cursor-pointer">
                        {mediaPreview ? (
                          mediaType === 'video' ? (
                            <div className="relative">
                              <video src={mediaPreview} className="max-h-48 mx-auto rounded-lg" />
                              <Badge className="absolute top-2 right-2 bg-red-500">Video</Badge>
                            </div>
                          ) : (
                            <img src={mediaPreview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                          )
                        ) : (
                          <>
                            <div className="flex justify-center gap-4 mb-3">
                              <ImageIcon className="h-10 w-10 text-gray-500" />
                              <FileVideo className="h-10 w-10 text-gray-500" />
                            </div>
                            <p className="text-gray-400 text-sm">Click to upload image or video</p>
                            <p className="text-gray-500 text-xs mt-1">JPEG, PNG, WEBP, MP4, WEBM</p>
                          </>
                        )}
                      </label>
                    </div>
                    {mediaFile && (
                      <div className="flex items-center gap-2 text-sm">
                        <Badge className={mediaType === 'video' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}>
                          {mediaType === 'video' ? <FileVideo className="h-3 w-3 mr-1" /> : <ImageIcon className="h-3 w-3 mr-1" />}
                          {mediaType}
                        </Badge>
                        <span className="text-gray-400 truncate">{mediaFile.name}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Sport</Label>
                        <Select value={mediaForm.sport} onValueChange={(v) => setMediaForm({ ...mediaForm, sport: v })}>
                          <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                            <SelectItem value="Basketball">Basketball</SelectItem>
                            <SelectItem value="Football">Football</SelectItem>
                            <SelectItem value="Soccer">Soccer</SelectItem>
                            <SelectItem value="Volleyball">Volleyball</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Analysis Type</Label>
                        <Select value={mediaForm.analysis_type} onValueChange={(v) => setMediaForm({ ...mediaForm, analysis_type: v })}>
                          <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                            <SelectItem value="general">{mediaType === 'video' ? 'Highlights' : 'General Analysis'}</SelectItem>
                            <SelectItem value="play_breakdown">{mediaType === 'video' ? 'Full Game' : 'Play Breakdown'}</SelectItem>
                            <SelectItem value="player_focus">Player Tracking</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-300">Player Focus (Optional)</Label>
                      <Input value={mediaForm.player_focus} onChange={(e) => setMediaForm({ ...mediaForm, player_focus: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white" placeholder="Player name to focus on" />
                    </div>
                    <div>
                      <Label className="text-gray-300">Additional Context</Label>
                      <Input value={mediaForm.context} onChange={(e) => setMediaForm({ ...mediaForm, context: e.target.value })}
                        className="bg-rooam-black border-rooam-gray-light text-white" placeholder="Optional context about the game" />
                    </div>
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" disabled={loading || !mediaFile}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} 
                      Analyze {mediaType === 'video' ? 'Video' : 'Image'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-green-400" /> 
                    {mediaType === 'video' ? 'Video' : 'Image'} Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  {mediaAnalysis ? (
                    <div className="space-y-3 sm:space-y-4">
                      {/* Top Plays Section */}
                      {mediaAnalysis.top_plays && mediaAnalysis.top_plays.length > 0 && (
                        <div className="bg-gradient-to-r from-rooam-blue/20 to-purple-500/20 rounded-lg p-3 sm:p-4 border border-rooam-blue/30">
                          <h4 className="text-white font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                            <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" /> TOP PLAYS
                          </h4>
                          <div className="space-y-2 sm:space-y-3 max-h-64 sm:max-h-none overflow-y-auto">
                            {mediaAnalysis.top_plays.slice(0, 6).map((play, i) => (
                              <div key={i} className="bg-rooam-black/50 rounded-lg p-2 sm:p-3 border border-rooam-gray-light">
                                <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <Badge className={`flex-shrink-0 ${play.excitement >= 9 ? 'bg-yellow-500 text-black' : 'bg-rooam-blue/20 text-rooam-blue'}`}>
                                      #{i + 1}
                                    </Badge>
                                    <span className="text-white font-medium text-sm truncate">{play.title}</span>
                                  </div>
                                  {play.excitement && (
                                    <div className="flex items-center gap-0.5 flex-shrink-0">
                                      {[...Array(Math.min(Math.ceil(play.excitement / 2), 5))].map((_, j) => (
                                        <Star key={j} className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                  {play.timestamp && (
                                    <span className="text-xs text-rooam-blue">⏱ {play.timestamp}</span>
                                  )}
                                  {play.play_type && (
                                    <Badge className="text-xs bg-purple-500/20 text-purple-400">{play.play_type}</Badge>
                                  )}
                                </div>
                                {play.description && (
                                  <p className="text-xs sm:text-sm text-gray-400 mt-1 line-clamp-2">{play.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Highlight Reel Preview */}
                      {mediaAnalysis.highlight_clips && mediaAnalysis.highlight_clips.length > 0 && (
                        <div className="bg-rooam-black rounded-lg p-3 sm:p-4 border border-rooam-gray-light">
                          <h4 className="text-white font-semibold mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                            <Film className="h-4 w-4 sm:h-5 sm:w-5 text-rooam-blue" /> HIGHLIGHT REEL
                          </h4>
                          <div className="relative bg-rooam-gray-light/30 rounded-lg p-2 mb-3">
                            <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
                              {mediaAnalysis.highlight_clips.map((clip, i) => (
                                <div 
                                  key={i}
                                  className={`flex-shrink-0 w-24 h-16 rounded ${
                                    clip.excitement >= 9 ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
                                    clip.type === 'slowmo' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                                    'bg-gradient-to-br from-rooam-blue to-blue-600'
                                  } flex items-center justify-center text-center p-1`}
                                >
                                  <span className="text-xs text-white font-medium truncate">{clip.title}</span>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>0:00</span>
                              <span>{Math.floor(mediaAnalysis.highlight_clips.reduce((acc, c) => acc + (c.end - c.start), 0) / 60)}:{String(mediaAnalysis.highlight_clips.reduce((acc, c) => acc + (c.end - c.start), 0) % 60).padStart(2, '0')}</span>
                            </div>
                          </div>
                          <Button 
                            className="w-full bg-rooam-blue text-black"
                            onClick={() => {
                              setHighlightReel({
                                id: mediaAnalysis.id,
                                title: `${mediaForm.sport} Highlights`,
                                clips: mediaAnalysis.highlight_clips,
                                duration: mediaAnalysis.highlight_clips.reduce((acc, c) => acc + (c.end - c.start), 0),
                                style: 'hype'
                              });
                              setShowEditor(true);
                              setActiveTab('reels');
                            }}
                          >
                            <Play className="h-4 w-4 mr-2" /> Open in Timeline Editor
                          </Button>
                        </div>
                      )}

                      {/* Full Analysis Text */}
                      <details className="bg-rooam-black rounded-lg">
                        <summary className="p-3 text-gray-400 cursor-pointer hover:text-white text-sm">
                          View Full Analysis Text
                        </summary>
                        <div className="p-4 pt-0 max-h-48 overflow-y-auto">
                          <pre className="whitespace-pre-wrap text-xs text-gray-400 font-sans">{mediaAnalysis.analysis}</pre>
                        </div>
                      </details>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 border-rooam-blue text-rooam-blue"
                          onClick={() => handleShareAnalysis(mediaAnalysis.id)}
                        >
                          {copiedId === mediaAnalysis.id ? <Check className="h-4 w-4 mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
                          {copiedId === mediaAnalysis.id ? 'Copied!' : 'Share'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="flex justify-center gap-2 mb-4">
                        <ImageIcon className="h-8 w-8 text-gray-500" />
                        <FileVideo className="h-8 w-8 text-gray-500" />
                      </div>
                      <p className="text-gray-400">Upload an image or video for advanced stats</p>
                      <p className="text-gray-500 text-sm mt-2">Get TOP PLAYS + Auto-Generated Highlight Reel</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Game Log Analysis Tab */}
          <TabsContent value="gamelog">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-rooam-blue" /> Analyze Game Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleGameLogAnalysis} className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Select Tournament *</Label>
                      <Select value={selectedTournament} onValueChange={(v) => { setSelectedTournament(v); setSelectedMatch(''); }}>
                        <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue placeholder="Choose a tournament" /></SelectTrigger>
                        <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-64">
                          {tournaments.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              <div className="flex items-center gap-2">
                                <span>{t.name}</span>
                                <Badge className="text-xs bg-rooam-blue/20 text-rooam-blue">{t.completed_matches}/{t.total_matches}</Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedTournamentData && (
                      <div className="bg-rooam-black rounded-lg p-3 border border-rooam-gray-light">
                        <p className="text-sm text-gray-300"><strong>{selectedTournamentData.name}</strong></p>
                        <p className="text-xs text-gray-500">{selectedTournamentData.sport} • {selectedTournamentData.bracket_type} • {selectedTournamentData.num_teams} teams</p>
                        <p className="text-xs text-rooam-blue mt-1">{selectedTournamentData.completed_matches} matches completed</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-gray-300">Specific Match (Optional)</Label>
                      <Input value={selectedMatch} onChange={(e) => setSelectedMatch(e.target.value)}
                        className="bg-rooam-black border-rooam-gray-light text-white" placeholder="Leave empty to analyze all matches" />
                    </div>
                    <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" disabled={loading || !selectedTournament}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} Analyze Game Log
                    </Button>
                  </form>
                </CardContent>
              </Card>
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Target className="h-5 w-5 text-green-400" /> Tournament Analysis</CardTitle></CardHeader>
                <CardContent>
                  {gameLogAnalysis ? (
                    <div className="bg-rooam-black rounded-lg p-4 max-h-96 overflow-y-auto">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-white">{gameLogAnalysis.tournament_name}</h4>
                        <Badge className="bg-rooam-blue/20 text-rooam-blue text-xs">{gameLogAnalysis.matches_analyzed} matches</Badge>
                      </div>
                      <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans">{gameLogAnalysis.analysis}</pre>
                    </div>
                  ) : (
                    <div className="text-center py-12"><Trophy className="h-12 w-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-400">Select a tournament to analyze game logs</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Highlight Reels Tab */}
          <TabsContent value="reels">
            {showEditor && highlightReel ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Film className="h-5 w-5 text-rooam-blue" /> Timeline Editor
                  </h2>
                  <Button variant="outline" className="border-rooam-gray-light text-gray-300" onClick={() => setShowEditor(false)}>
                    Back to Generator
                  </Button>
                </div>
                <TimelineEditor
                  initialClips={highlightReel.clips || []}
                  duration={highlightReel.duration || 60}
                  style={highlightReel.style || 'hype'}
                  title={highlightReel.title || 'Highlight Reel'}
                  reelId={highlightReel.id}
                  videoUrl={highlightReel.video_url}
                  thumbnailUrl={highlightReel.thumbnail_url}
                  onSave={async (data) => {
                    try {
                      await fetch(`${API_URL}/api/ai/highlight-reels/${highlightReel.id}/clips`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify(data.clips)
                      });
                      toast.success('Reel saved!');
                    } catch (e) {
                      toast.error('Failed to save');
                    }
                  }}
                  onShare={() => handleShareReel(highlightReel.id)}
                />
              </div>
            ) : (
              <>
                <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
                  <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                    <CardHeader>
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Film className="h-5 w-5 text-rooam-blue" /> Generate Highlight Reel
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleGenerateReel} className="space-y-4">
                        <div>
                          <Label className="text-gray-300">Reel Type</Label>
                          <Select value={reelForm.reel_type} onValueChange={(v) => setReelForm({ ...reelForm, reel_type: v })}>
                            <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                              <SelectItem value="tournament">Tournament Highlights</SelectItem>
                              <SelectItem value="player">Player Highlights</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {reelForm.reel_type === 'tournament' && (
                          <div>
                            <Label className="text-gray-300">Tournament</Label>
                            <Select value={reelForm.tournament_id} onValueChange={(v) => setReelForm({ ...reelForm, tournament_id: v })}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue placeholder="Select tournament" /></SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-64">
                                {tournaments.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {reelForm.reel_type === 'player' && (
                          <div>
                            <Label className="text-gray-300">Athlete</Label>
                            <Select value={reelForm.athlete_id} onValueChange={(v) => setReelForm({ ...reelForm, athlete_id: v })}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue placeholder="Select athlete" /></SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-64">
                                {athletes.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name} - {a.position}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-300">Duration</Label>
                            <Select value={String(reelForm.duration)} onValueChange={(v) => setReelForm({ ...reelForm, duration: parseInt(v) })}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="30">30 seconds</SelectItem>
                                <SelectItem value="60">60 seconds</SelectItem>
                                <SelectItem value="90">90 seconds</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-300">Style</Label>
                            <Select value={reelForm.style} onValueChange={(v) => setReelForm({ ...reelForm, style: v })}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="hype">Hype / High Energy</SelectItem>
                                <SelectItem value="cinematic">Cinematic</SelectItem>
                                <SelectItem value="recap">Game Recap</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button type="submit" className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" disabled={loading}>
                          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Film className="h-4 w-4 mr-2" />} Generate Reel
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                  <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                    <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Star className="h-5 w-5 text-yellow-400" /> Highlight Reel</CardTitle></CardHeader>
                    <CardContent>
                      {highlightReel ? (
                        <div className="space-y-4">
                          <div className="bg-rooam-black rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-white">{highlightReel.title}</h4>
                              <div className="flex gap-2">
                                <Badge className="bg-purple-500/20 text-purple-400">{highlightReel.style}</Badge>
                                <Badge className="bg-rooam-blue/20 text-rooam-blue">{highlightReel.duration}s</Badge>
                              </div>
                            </div>
                            <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans max-h-48 overflow-y-auto">{highlightReel.script}</pre>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              className="flex-1 bg-rooam-blue text-black" 
                              onClick={() => setShowEditor(true)}
                            >
                              <Play className="h-4 w-4 mr-2" /> Open Editor
                            </Button>
                            <Button 
                              variant="outline" 
                              className="border-rooam-blue text-rooam-blue"
                              onClick={() => handleShareReel(highlightReel.id)}
                            >
                              {copiedId === highlightReel.id ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12"><Film className="h-12 w-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-400">Generate a highlight reel to start editing</p></div>
                      )}
                    </CardContent>
                  </Card>
                </div>
                {previousReels.length > 0 && (
                  <Card className="mt-6 bg-rooam-gray-medium border-rooam-gray-light">
                    <CardHeader><CardTitle className="text-white text-lg">Your Reels</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {previousReels.slice(0, 6).map((reel) => (
                          <div key={reel.id} className="bg-rooam-black rounded-lg p-4 border border-rooam-gray-light hover:border-rooam-blue/50 transition-colors cursor-pointer group"
                            onClick={() => { setHighlightReel(reel); setShowEditor(true); }}>
                            <h4 className="font-semibold text-white mb-2 truncate">{reel.title}</h4>
                            <div className="flex gap-2 mb-2">
                              <Badge className="text-xs bg-purple-500/20 text-purple-400">{reel.style}</Badge>
                              <Badge className="text-xs bg-rooam-blue/20 text-rooam-blue">{reel.duration_seconds}s</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">{new Date(reel.created_at).toLocaleDateString()}</p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="opacity-0 group-hover:opacity-100 h-6 px-2"
                                onClick={(e) => { e.stopPropagation(); handleShareReel(reel.id); }}
                              >
                                {copiedId === reel.id ? <Check className="h-3 w-3" /> : <Link className="h-3 w-3" />}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Player Insights Tab */}
          <TabsContent value="player">
            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><User className="h-5 w-5 text-rooam-blue" /> Select Athlete</CardTitle></CardHeader>
                <CardContent>
                  {athletes.length === 0 ? (
                    <div className="text-center py-8"><User className="h-12 w-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-400">No athletes found</p></div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {athletes.map((athlete) => (
                        <div key={athlete.id}
                          className={`bg-rooam-black rounded-lg p-4 border cursor-pointer transition-all ${selectedAthlete === athlete.id ? 'border-rooam-blue bg-rooam-blue/10' : 'border-rooam-gray-light hover:border-rooam-blue/50'}`}
                          onClick={() => handlePlayerInsights(athlete.id)}>
                          <div className="flex items-center gap-3">
                            <img src={athlete.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`} alt={athlete.name} className="w-12 h-12 rounded-full" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">{athlete.name}</h4>
                              <p className="text-sm text-gray-400">{athlete.position} • {athlete.team}</p>
                            </div>
                            <Badge className="bg-rooam-blue/20 text-rooam-blue text-xs">{athlete.sport}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-green-400" /> Player Insights</CardTitle></CardHeader>
                <CardContent>
                  {loading && selectedAthlete ? (
                    <div className="text-center py-12"><Loader2 className="h-8 w-8 text-rooam-blue animate-spin mx-auto mb-4" /><p className="text-gray-400">Generating insights...</p></div>
                  ) : playerInsights ? (
                    <div className="bg-rooam-black rounded-lg p-4 max-h-96 overflow-y-auto">
                      <h4 className="font-semibold text-white mb-3">{playerInsights.athlete_name}</h4>
                      <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans">{playerInsights.insights}</pre>
                    </div>
                  ) : (
                    <div className="text-center py-12"><TrendingUp className="h-12 w-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-400">Select an athlete for AI insights</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {previousAnalyses.length > 0 && (
          <Card className="mt-6 md:mt-8 bg-rooam-gray-medium border-rooam-gray-light">
            <CardHeader><CardTitle className="text-white text-lg">Recent Analyses</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {previousAnalyses.slice(0, 6).map((analysis) => (
                  <div key={analysis.id} className="bg-rooam-black rounded-lg p-4 border border-rooam-gray-light">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-rooam-blue/20 text-rooam-blue text-xs">{analysis.sport || analysis.type}</Badge>
                      {analysis.type && <Badge className="bg-gray-500/20 text-gray-400 text-xs">{analysis.type}</Badge>}
                    </div>
                    <p className="text-sm text-gray-300 line-clamp-2">{analysis.description || analysis.tournament_name || 'Analysis'}</p>
                    <p className="text-xs text-gray-500 mt-2">{new Date(analysis.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIAnalysis;
